import { Mail, Phone, Clock, MapPin } from "lucide-react";
import PageHero from "@/components/PageHero";
import { useSEO } from "@/lib/seo";
import { FIRM, OFFICE_IMAGE, POSTAL, PRACTICE_CONTACTS, SERVICES_CONTACT_HERO } from "@/lib/content";

function ContactBlock({ contact, testId }) {
  return (
    <div
      data-testid={testId}
      className="border border-sage-100 bg-white p-7 md:p-8"
    >
      <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-sage-700">
        <div>{contact.area}</div>
        <div>Enquiries</div>
      </div>
      <div className="mt-5 text-xl font-bold text-ink md:text-2xl">
        {contact.name}
      </div>
      <div className="mt-5 space-y-3 text-sm text-ink-soft">
        <a
          href={`tel:${contact.phone.replace(/\s/g, "")}`}
          className="flex items-center gap-3 hover:text-sage-700"
        >
          <Phone className="h-4 w-4" strokeWidth={1.5} />
          {contact.phone}
        </a>
        <a
          href={`mailto:${contact.email}`}
          className="flex items-center gap-3 hover:text-sage-700"
        >
          <Mail className="h-4 w-4" strokeWidth={1.5} />
          {contact.email}
        </a>
      </div>
    </div>
  );
}

export default function Contact() {
  useSEO({
    title: "Contact, Bellhouse Law",
    description:
      "Get in touch with Bellhouse Law. Separate contacts for Commercial Property & Business, and Residential Conveyancing.",
  });

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Let's begin a conversation."
        subtitle="Whether you need urgent advice or simply want to explore an idea, we'd love to hear from you."
        image={SERVICES_CONTACT_HERO}
        testId="contact-hero"
      />

      <section className="section-pad bg-bone">
        <div className="container-narrow grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-5 space-y-8">
            <div>
              <span className="eyebrow">Reach us</span>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-ink md:text-4xl lg:text-[42px]">
                Direct lines, no gatekeepers.
              </h2>
            </div>

            <ContactBlock
              contact={PRACTICE_CONTACTS.commercial}
              testId="contact-block-commercial"
            />
            <ContactBlock
              contact={PRACTICE_CONTACTS.residential}
              testId="contact-block-residential"
            />

            <div className="grid grid-cols-1 gap-5 border-t border-sage-100 pt-8 sm:grid-cols-2">
              <div
                className="flex items-start gap-3 text-sm"
                data-testid="contact-postal"
              >
                <MapPin className="mt-0.5 h-4 w-4 text-sage-700" strokeWidth={1.5} />
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-muted">
                    Postal Address
                  </div>
                  <div className="mt-1 text-ink">
                    {POSTAL.line1}
                    <br />
                    {POSTAL.line2}
                    <br />
                    {POSTAL.country}
                  </div>
                </div>
              </div>
              <div
                className="flex items-start gap-3 text-sm"
                data-testid="contact-hours"
              >
                <Clock className="mt-0.5 h-4 w-4 text-sage-700" strokeWidth={1.5} />
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-muted">
                    Hours
                  </div>
                  <div className="mt-1 text-ink">{FIRM.hours}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="border border-sage-100 bg-white p-3 shadow-[0_24px_60px_-30px_rgba(26,30,25,0.18)] md:p-4">
              <div className="px-2 pb-2 pt-1 md:px-4">
                <span className="eyebrow">Online Enquiry</span>
                <h3 className="mt-3 text-2xl font-bold text-ink md:text-3xl">
                  Tell us a little about your matter.
                </h3>
              </div>
              <iframe
                title="Bellhouse Law, Online Enquiry"
                src={FIRM.intakeIframe}
                id="matter-intake-iframe"
                name="matter-intake-iframe"
                data-testid="matter-intake-iframe"
                style={{
                  display: "block",
                  width: "100%",
                  height: "900px",
                  border: "none",
                }}
              />
            </div>
            <p className="mt-4 text-xs text-ink-muted">
              Secure intake hosted by matter-intake.com.au. Your information is
              encrypted in transit.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
